import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactElement,
  JSXElementConstructor,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { updatePhotoCardOrder } from '@store/firebase/photocard/update-photocard';
import StaggerMotionWrapper from '@components/wrapper/StaggerMotionWrapper';

/** DnD 아이템 래퍼 — 드래그 중이면 placeholder 스타일 표시 */
const SortableItemWrapper = styled.div<{ $isDragging?: boolean }>`
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: none;

  border-radius: 4px;
  outline: 1px solid var(--lineColor);
  outline-offset: -1px;
  background: var(--subBgColor, rgba(0, 0, 0, 0.02));
  transition: box-shadow 0.15s, outline-color 0.15s;

  & img {
    -webkit-user-drag: none;
    user-drag: none;
    pointer-events: none;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
    opacity: 0.4;
    outline-color: var(--primaryColor, #6366f1);
    outline-style: dashed;
  `}
`;

/** DragOverlay 내부 — 포탈로 렌더링되어 overflow 컨테이너에 잘리지 않음 */
const OverlayWrapper = styled.div`
  border-radius: 4px;
  outline: 1px solid var(--primaryColor, #6366f1);
  outline-offset: -1px;
  background: var(--subBgColor, rgba(0, 0, 0, 0.02));
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35),
    0 0 8px rgba(99, 102, 241, 0.35);
  opacity: 0.92;
  transform: scale(1.08);
  cursor: grabbing;
  pointer-events: none;
`;

function GridDroppable({
  collectionPath,
  list,
  line,
  children,
  onDragStartCallback,
  onDragEndCallback,
  gap,
  minItemWidth,
  ...props
}: {
  collectionPath: string;
  list: any[];
  line: number;
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  onDragStartCallback?: () => void;
  onDragEndCallback?: () => void;
  gap?: string;
  /** 설정 시 auto-fill 반응형 그리드로 전환 (예: '80px') */
  minItemWidth?: string;
  [key: string]: any;
}) {
  const [orderList, setOrderList] = useState<any>([]);
  // Firestore 쓰기가 완료되기 전에 useEffect([list])가 orderList를 덮어쓰는
  // 레이스 컨디션을 방지하기 위한 플래그
  const isPendingRef = useRef(false);

  // DragOverlay용 활성 아이템 추적
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeItem = activeId
    ? orderList.find((item: any) => item.id === activeId)
    : null;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  // dnd-kit v4는 collision detection에 pointerCoordinates를 전달하지 않으므로
  // 마우스/터치 위치를 직접 추적하여 실시간 rect 기반 충돌 감지에 활용
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const onMouse = (e: MouseEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    const onTouch = (e: TouchEvent) => {
      if (e.touches[0]) {
        pointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('touchmove', onTouch, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('touchmove', onTouch);
    };
  }, []);

  // 실시간 getBoundingClientRect 기반 충돌 감지 (overflow:hidden / transform 부모에서도 정확)
  const realtimeCollision = useCallback((args: any) => {
    const { droppableContainers } = args;
    const pointer = pointerRef.current;

    if (pointer) {
      for (const container of droppableContainers) {
        const node = container.node?.current;
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        if (
          pointer.x >= rect.left &&
          pointer.x <= rect.right &&
          pointer.y >= rect.top &&
          pointer.y <= rect.bottom
        ) {
          return container.id;
        }
      }
    }

    return closestCenter(args);
  }, []);

  useEffect(() => {
    // list 복사해서 orderList에 할당
    // 단, Firestore 쓰기가 완료되기 전에 중간 상태로 덮어쓰는 것을 방지
    if (list && !isPendingRef.current) {
      const copyList = list.slice();
      setOrderList(copyList);
    }
  }, [list]);

  function handleDragStart(event) {
    setActiveId(event.active.id as string);
    onDragStartCallback?.();
  }

  function handleDragEnd(event) {
    setActiveId(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = active.data.current.sortable.index;
    const newIndex = over.data.current.sortable.index;

    if (oldIndex === newIndex) return;

    // state updater 밖에서 reorderList를 계산하고 사이드 이펙트를 분리
    const reorderList = arrayMove(orderList, oldIndex, newIndex);
    setOrderList(reorderList);

    // Firestore 쓰기가 완료되기 전에 useEffect([list])가 중간 상태로 덮어쓰지 않도록 플래그 설정
    isPendingRef.current = true;
    onDragEndCallback?.();
    updatePhotoCardOrder({ collectionPath, orderList: reorderList })
      .then((promises) => Promise.all(promises as Promise<any>[]))
      .finally(() => {
        isPendingRef.current = false;
      });
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={realtimeCollision as any}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={orderList.map((item) => item.id)} strategy={rectSortingStrategy}>
          <StaggerMotionWrapper
            line={line}
            gap={gap}
            style={{
              width: '100%',
              ...(minItemWidth && {
                gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}, 1fr))`,
              }),
            }}
          >
            {orderList.map((list, index) => (
              <SortableItem
                key={list.id}
                id={list.id}
                data={list}
                collectionPath={collectionPath}
                {...props}
              >
                {React.cloneElement(children, { ...props })}
              </SortableItem>
            ))}
          </StaggerMotionWrapper>
        </SortableContext>

        {/* DragOverlay: Portal로 body에 렌더 — overflow:hidden / transform 부모에 잘리지 않도록 */}
        {ReactDOM.createPortal(
          <DragOverlay dropAnimation={null}>
            {activeItem ? (
              <OverlayWrapper>
                {React.cloneElement(children, {
                  id: activeItem.id,
                  data: activeItem,
                  collectionPath,
                  ...props,
                })}
              </OverlayWrapper>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </>
  );
}

function SortableItem({
  id,
  data,
  collectionPath,
  ...props
}: {
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  collectionPath: string;
  id: string;
  data: any;
}): any {
  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const baseTransform = CSS.Transform.toString(transform);
  const style: React.CSSProperties = {
    transform: baseTransform || undefined,
    transition: isDragging ? 'none' : transition,
    cursor: 'grab',
  };

  return (
    <SortableItemWrapper
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
    >
      {React.cloneElement(props.children, {
        id,
        data,
        collectionPath,
        ...props,
        ...listeners,
        ...attributes,
      })}
    </SortableItemWrapper>
  );
}

export default GridDroppable;
